/**
 * 임시 비밀번호 1회 표시 모달.
 * Figma 비밀번호 초기화 완료 / 계정 생성 완료에서 재사용 — RDS Dialog.
 */
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { popupPrimaryButtonClassName } from '@/shared/components/ui/popup-styles';
import { useToast } from '@/shared/utils/toast';
import { Check, TriangleAlert } from 'lucide-react';
import React from 'react';

export type PasswordResetResultModalProps = {
  open: boolean;
  temporaryPassword: string;
  onClose: () => void;
  /** 기본: 비밀번호 초기화 완료 타이틀 */
  title?: string;
  /** 기본: 초기화용 안내 문구 (email 기반) */
  email?: string;
  /** 지정 시 기본 설명을 대체 */
  description?: React.ReactNode;
};

export const PasswordResetResultModal: React.FC<
  PasswordResetResultModalProps
> = ({
  open,
  temporaryPassword,
  onClose,
  title = '비밀번호가 초기화되었습니다',
  email,
  description,
}) => {
  const toast = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(temporaryPassword);
      toast.success('임시 비밀번호를 복사했습니다.');
    } catch {
      toast.error('복사에 실패했습니다.');
    }
  };

  const resolvedDescription =
    description ??
    (email ? (
      <>
        <span className="block">{email} 계정의 새 임시 비밀번호입니다.</span>
        <span className="block">기존 로그인 세션은 모두 만료되었습니다.</span>
      </>
    ) : null);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent showCloseButton={false} className="max-w-[480px]">
        <div className="flex justify-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-ruda-success-50 text-ruda-success">
            <Check className="h-6 w-6" strokeWidth={2.5} aria-hidden />
          </div>
        </div>

        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {resolvedDescription ? (
            <DialogDescription>{resolvedDescription}</DialogDescription>
          ) : null}
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 rounded-12 border border-border bg-muted/60 px-4 py-3.5">
          <p className="min-w-0 truncate font-mono text-lg font-semibold tracking-tight text-foreground">
            {temporaryPassword}
          </p>
          <Button
            type="button"
            size="sm"
            className="ruda-button shrink-0"
            onClick={handleCopy}
          >
            복사
          </Button>
        </div>

        <div className="flex items-start gap-2 rounded-12 bg-ruda-error-50 px-3.5 py-2.5 text-sm text-ruda-error">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p className="min-w-0 flex-1">
            이 창을 닫으면 비밀번호를 다시 확인할 수 없습니다. 분실 시 비밀번호
            초기화를 다시 진행해야 합니다.
          </p>
        </div>

        <DialogFooter layout="vertical">
          <Button
            type="button"
            className={popupPrimaryButtonClassName}
            onClick={onClose}
          >
            확인 후 닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 비밀번호 초기화 완료 — Figma 샘플 */
export const MOCK_TEMPORARY_PASSWORD = 'Tk4@wn8Zr2#p';

/** 계정 생성 완료 — Figma 샘플 */
export const MOCK_CREATE_TEMPORARY_PASSWORD = 'Rd7#kp2Xq9!m';
